import { supabase } from "@/integrations/supabase/client";

export const CLEANUP_ORG_ID = "a4832e26-e9d3-4506-a0a0-e7cff549e3cf";
export const CLEANUP_CUTOFF = "2026-08-01";
export const CLEANUP_CONFIRMATION = "DELETE GOOD SAMARITAN DATA BEFORE 2026-08-01";

export interface CleanupBlocker {
  table: string;
  reason?: string;
  records?: number;
  record_id?: string;
  constraint?: string;
}

export interface CleanupPlan {
  org_id: string;
  cutoff_date: string;
  dry_run: boolean;
  counts: Record<string, number>;
  blockers: CleanupBlocker[];
  digest: string;
  total_records: number;
  required_confirmation?: string;
  cleanup_run_id?: string;
}

export class CleanupCompletedError extends Error {}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Fail closed on an older deployment or an unexpected RPC response. */
export function parseCleanupPlan(value: unknown, preview: boolean): CleanupPlan {
  if (!isRecord(value) || value.org_id !== CLEANUP_ORG_ID || value.cutoff_date !== CLEANUP_CUTOFF
    || value.dry_run !== preview || !isRecord(value.counts) || !Array.isArray(value.blockers)
    || typeof value.digest !== "string" || !/^[a-f0-9]{32}$/.test(value.digest)
    || !Number.isSafeInteger(value.total_records) || (value.total_records as number) < 0
    || (preview && value.required_confirmation !== CLEANUP_CONFIRMATION)
    || (!preview && (typeof value.cleanup_run_id !== "string" || !value.cleanup_run_id))) {
    throw new Error("The cleanup response could not be verified. Refresh the preview before continuing.");
  }
  const counts = value.counts;
  if (Object.values(counts).some(count => !Number.isSafeInteger(count) || (count as number) < 0)
    || Object.values(counts).reduce<number>((total, count) => total + (count as number), 0) !== value.total_records
    || value.blockers.some(blocker => !isRecord(blocker) || typeof blocker.table !== "string"
      || (blocker.records !== undefined && (!Number.isSafeInteger(blocker.records) || (blocker.records as number) < 0))
      || ["reason", "record_id", "constraint"].some(key => blocker[key] !== undefined && typeof blocker[key] !== "string"))) {
    throw new Error("The cleanup counts or blockers could not be verified. Refresh the preview before continuing.");
  }
  return value as unknown as CleanupPlan;
}

export async function canUseFinanceCleanup(orgId: string, userId: string): Promise<boolean> {
  if (orgId !== CLEANUP_ORG_ID) return false;
  const [admin, readonly] = await Promise.all([
    // Same DB-role + membership rule as the cleanup's finance gate. Unlike
    // can_access_finance_org's SQL array overloads, this RPC is unambiguous to PostgREST.
    supabase.rpc("has_role_in_org", { _org_id: orgId, _user_id: userId, _roles: ["admin", "super_admin"] }),
    supabase.rpc("is_readonly_app_user_for_org", { _org_id: orgId, _user_id: userId }),
  ]);
  if (admin.error) throw admin.error;
  if (readonly.error) throw readonly.error;
  return admin.data === true && readonly.data === false;
}

async function callCleanup(orgId: string, confirmation: string | null, digest: string | null) {
  if (orgId !== CLEANUP_ORG_ID) throw new Error("This cleanup is available only for Good Samaritan.");
  const { data, error } = await supabase.rpc("finance_clear_pre_go_live_data", {
    p_org_id: orgId, p_confirmation: confirmation, p_expected_digest: digest,
  });
  if (error) {
    if (error.code === "22023" && error.message === "The one-time Good Samaritan cleanup has already completed") {
      throw new CleanupCompletedError("The one-time Good Samaritan cleanup has already completed.");
    }
    throw error;
  }
  return parseCleanupPlan(data, confirmation === null);
}

export function previewFinanceCleanup(orgId: string) {
  return callCleanup(orgId, null, null);
}

export function executeFinanceCleanup(orgId: string, plan: CleanupPlan, confirmation: string) {
  if (plan.org_id !== orgId || plan.dry_run !== true || plan.required_confirmation !== CLEANUP_CONFIRMATION
    || plan.blockers.length !== 0 || plan.total_records === 0 || confirmation !== CLEANUP_CONFIRMATION) {
    throw new Error("A clear preview and the exact confirmation phrase are required.");
  }
  return callCleanup(orgId, confirmation, plan.digest);
}

/** Staff-facing names; future categories remain visible rather than being omitted. */
export function cleanupCategoryLabel(table: string): string {
  const name = table.replace(/^public\./, "").replace(/^finance_/, "");
  const labels: Record<string, string> = {
    journal_entries: "Journal entries", journal_lines: "General ledger lines",
    ap_bills: "Bills", ap_bill_lines: "Bill lines", ap_payment_batches: "Bill payment batches",
    ap_payments: "Bill payments", ar_invoices: "Invoices", ar_invoice_lines: "Invoice lines",
    ar_receipts: "Customer receipts", ar_credit_memos: "Credit memos", ar_write_offs: "Invoice write-offs",
    po_headers: "Purchase orders", po_lines: "Purchase order lines", queue: "Posting log records",
    object_links: "Provider links", integration_object_mappings: "Integration links",
    document_references: "Document references", approval_requests: "Approval requests",
    taggings: "Tag assignments", linked_records: "Linked records", media_library: "Media links",
  };
  return labels[name] ?? name.replace(/_/g, " ").replace(/^./, first => first.toUpperCase());
}

export function cleanupBlockerReason(blocker: CleanupBlocker): string {
  if (blocker.reason === "selected child belongs to a preserved transaction") return "Belongs to a transaction that must be kept";
  if (blocker.reason?.includes("reference")) return "Links a record that must be kept to a record selected for deletion";
  return "A record that must be kept depends on a record selected for deletion";
}
